/**
 * Scene Manager - Handles Three.js scene, camera, lighting, and environment
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { modelLoader } from '../systems/ModelLoader.js';
import { IslandGenerator } from '../systems/IslandGenerator.js';
import { isMobileDevice } from '../utils/device.js';
import { GAME_BOUNDARY_SIZE } from '../utils/constants.js';
// Three.js water helper (examples)
import { Water } from 'three/examples/jsm/objects/Water.js';
// GLB loader
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SimplifyModifier } from 'three/examples/jsm/modifiers/SimplifyModifier.js';

export class SceneManager {
    constructor(physicsSystem = null) {
        this.scene = null;
        this.camera = null;
        this.followTarget = null;

        this.defaultCameraConfig = {
            offset: new THREE.Vector3(0, 25, 15), // 降低相机高度，拉近视角
            fov: 75
        };
        this.mobileCameraConfig = {
            offset: new THREE.Vector3(0, 40, 25), // 移动端也降低视角
            fov: 82
        };
        this.cameraOffset = this.defaultCameraConfig.offset.clone();
        this.currentCameraConfig = 'desktop';
        this.lookAtOffset = new THREE.Vector3(0, 0, 0);

        // Physics system reference
        this.physicsSystem = physicsSystem;

        // Environment objects
        this.ocean = null; // Will be either Water or Mesh
        this.skybox = null;

        // Lighting
        this.ambientLight = null;
        this.directionalLight = null;
        this.fillLight = null;
        this.topLight = null;

        // Camera smoothing（增大平滑系数以减少抖动，并缓存lookAt状态）
        this.cameraLerpFactor = 0.12;

        // 海岛生成器
        this.islandGenerator = new IslandGenerator();
        this.lookAtLerpFactor = 0.2;
        this._currentLookAt = new THREE.Vector3();

        this.ambientIslands = [];
        this.centralIslandBounds = null;
        this.simplifyModifier = new SimplifyModifier();
        this.centralIslandBounds = null;

        // LOD (Level of Detail) 系统
        this.centralIslandMesh = null; // 中心岛屿mesh引用
        this.islandDetailLevel = 'full'; // 'full', 'reduced'
        this.lastLODUpdate = 0;
        this.lodUpdateInterval = 500; // 每500ms更新一次LOD

        // Store physics bodies for cleanup
        this.physicsBodies = [];

        // Feature toggles from URL params (e.g., ?ocean=static to disable dynamic water)
        const _params = new URLSearchParams(window.location.search);
        this.enableDynamicOcean = !(_params.get('ocean') === 'static' || _params.get('ocean') === 'off');
        // 默认使用精确的岛屿物理碰撞检测，只有在明确指定时才使用简化的圆柱体近似
        this.useSimplifiedIslandPhysics = _params.get('islandPhysics') === 'simple';

        this.applyCameraDeviceConfig();
    }

    // 调试函数已移除，避免生产环境覆盖页面或抛错

    async init() {

        // Create scene
        this.scene = new THREE.Scene();
        // Fog disabled as requested

        // Setup camera
        this.setupCamera();

        // Setup lighting
        this.setupLighting();

        // Create environment
        await this.createEnvironment();

    }

    setupCamera() {
        // Create perspective camera
        const aspect = window.innerWidth / window.innerHeight;
        const initialFov = this.currentCameraConfig === 'mobile' ? this.mobileCameraConfig.fov : this.defaultCameraConfig.fov;
        this.camera = new THREE.PerspectiveCamera(initialFov, aspect, 0.1, 2000); // 增加远裁剪面以看到1500x1500海面
        this.applyCameraDeviceConfig();

        // 设置相机可以看到 layer 0 和 layer 1（layer 1 用于伤害数字等UI元素）
        this.camera.layers.enable(0);
        this.camera.layers.enable(1);

        // Position camera above and behind the center
        this.camera.position.copy(this.cameraOffset);
        this.camera.lookAt(0, 0, 0);
    }

    setupLighting() {
        // 增强环境光照 - 提供更明亮的基础照明
        this.ambientLight = new THREE.AmbientLight(0x87CEEB, 0.6); // 天空蓝色，更高强度
        this.scene.add(this.ambientLight);

        // 主阳光 - 从角度照射，适度强度
        this.directionalLight = new THREE.DirectionalLight(0xffffff, 1.0);
        this.directionalLight.position.set(100, 120, 80); // 调整角度，更斜的光线
        this.directionalLight.castShadow = true;

        // 配置阴影属性 - 提高阴影质量以减少格栅化
        this.directionalLight.shadow.mapSize.width = 2048;  // 提高到2048以减少锯齿
        this.directionalLight.shadow.mapSize.height = 2048; // 提高到2048以减少锯齿
        this.directionalLight.shadow.camera.near = 0.5;
        this.directionalLight.shadow.camera.far = 200;      // 减小范围以提高精度
        this.directionalLight.shadow.camera.left = -60;     // 缩小范围以提高精度
        this.directionalLight.shadow.camera.right = 60;
        this.directionalLight.shadow.camera.top = 60;
        this.directionalLight.shadow.camera.bottom = -60;

        // 添加阴影偏移以减少阴影痤疮和锯齿
        this.directionalLight.shadow.bias = -0.0005;
        this.directionalLight.shadow.normalBias = 0.02;

        // 使用PCF阴影过滤以获得更平滑的阴影边缘
        this.directionalLight.shadow.radius = 4;
        this.directionalLight.shadow.blurSamples = 25;

        this.scene.add(this.directionalLight);

        // 添加补充光源 - 从另一个角度照射
        this.fillLight = new THREE.DirectionalLight(0xfff8dc, 0.4); // 暖色补光
        this.fillLight.position.set(-80, 60, -60); // 从对角照射
        this.fillLight.castShadow = false; // 补光不产生阴影
        this.scene.add(this.fillLight);

        // 添加顶部补光 - 减少阴影过暗
        this.topLight = new THREE.DirectionalLight(0xe6f3ff, 0.3); // 冷色顶光
        this.topLight.position.set(0, 150, 0); // 从正上方照射
        this.topLight.castShadow = false;
        this.scene.add(this.topLight);

        console.log('✅ 已设置明亮的多角度光照系统');
    }

    async createEnvironment() {
        await this.createOcean();
        this.createSkybox();
        // this.createGrid(); // Grid helper disabled
        this.createGameBoundaries();
        await this.createReferenceObjects();
    }

    createGameBoundaries() {
        const boundarySize = GAME_BOUNDARY_SIZE; // 游戏区域大小，边界在±GAME_BOUNDARY_SIZE

        // 创建红色线框材质
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0xFF0000, // 红色
            linewidth: 3 // 线宽
        });

        // 创建边界线几何体
        const points = [
            // 创建正方形边界的点
            new THREE.Vector3(-boundarySize, 0, -boundarySize), // 左上角
            new THREE.Vector3(boundarySize, 0, -boundarySize),  // 右上角
            new THREE.Vector3(boundarySize, 0, boundarySize),   // 右下角
            new THREE.Vector3(-boundarySize, 0, boundarySize),  // 左下角
            new THREE.Vector3(-boundarySize, 0, -boundarySize)  // 回到起点闭合
        ];

        const boundaryGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const boundaryLine = new THREE.Line(boundaryGeometry, lineMaterial);
        boundaryLine.name = 'gameBoundaryLine';
        this.scene.add(boundaryLine);

        // 添加垂直线条增加可见性
        const verticalLines = [
            // 四个角的垂直线
            { start: [-boundarySize, 0, -boundarySize], end: [-boundarySize, 5, -boundarySize] },
            { start: [boundarySize, 0, -boundarySize], end: [boundarySize, 5, -boundarySize] },
            { start: [boundarySize, 0, boundarySize], end: [boundarySize, 5, boundarySize] },
            { start: [-boundarySize, 0, boundarySize], end: [-boundarySize, 5, boundarySize] }
        ];

        verticalLines.forEach((line, index) => {
            const verticalPoints = [
                new THREE.Vector3(...line.start),
                new THREE.Vector3(...line.end)
            ];
            const verticalGeometry = new THREE.BufferGeometry().setFromPoints(verticalPoints);
            const verticalLine = new THREE.Line(verticalGeometry, lineMaterial);
            verticalLine.name = `verticalBoundary_${index}`;
            this.scene.add(verticalLine);
        });

        // 添加边界标记
        this.addBoundaryMarkers(boundarySize);
    }

    addBoundaryMarkers(boundarySize) {
        // 在边界外添加小的标记物体，表示这是游戏区域边界
        const markerGeometry = new THREE.ConeGeometry(1, 3, 4);
        const markerMaterial = new THREE.MeshBasicMaterial({ color: 0xFF4444 });

        const markers = [
            { pos: [0, 1.5, -boundarySize - 5], name: 'North Boundary' },
            { pos: [0, 1.5, boundarySize + 5], name: 'South Boundary' },
            { pos: [boundarySize + 5, 1.5, 0], name: 'East Boundary' },
            { pos: [-boundarySize - 5, 1.5, 0], name: 'West Boundary' }
        ];

        markers.forEach((marker, index) => {
            const markerMesh = new THREE.Mesh(markerGeometry, markerMaterial);
            markerMesh.position.set(...marker.pos);
            markerMesh.name = `boundaryMarker_${index}`;
            this.scene.add(markerMesh);
        });
    }

    async createOcean() {
        // Prefer a subtle dynamic ocean using Three.js Water; fallback to static plane on error
        // 大幅扩大海面平面，保证视野再偏移也不会看到边缘
        const size = 2400;
        const geometry = new THREE.PlaneGeometry(size, size, 64, 64); // 增加分段数以正确显示大平面

        try {
            if (!this.enableDynamicOcean) throw new Error('Dynamic ocean disabled');
            // Load water normal map (use CDN to avoid bundling assets). Falls back to static if it fails.
            const waterNormals = await new Promise((resolve, reject) => {
                // Use shared loading manager for progress aggregation
                const loader = new THREE.TextureLoader(modelLoader.loadingManager);
                loader.load(
                    'https://threejs.org/examples/textures/waternormals.jpg',
                    (tex) => {
                        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
                        resolve(tex);
                    },
                    undefined,
                    (err) => reject(err)
                );
            });

            const sunDir = this.directionalLight
                ? this.directionalLight.position.clone().normalize()
                : new THREE.Vector3(1, 1, 1).normalize();

            const water = new Water(geometry, {
                textureWidth: 256,
                textureHeight: 256,
                waterNormals,
                sunDirection: sunDir,
                // 深蓝色高光，避免偏白
                sunColor: 0x103A6E,
                // 稍微浅一点的深海蓝
                waterColor: 0x2B61A8,
                // 适度波动与反射效果
                distortionScale: 0.08,
                alpha: 1.0,
                fog: !!this.scene.fog
            });

            water.rotation.x = -Math.PI / 2;
            water.position.y = -0.1; // 稍微降低海面高度，避免与沙滩Z-fighting
            // Avoid dynamic shadows on water for performance
            water.receiveShadow = false;

            // 重写 Water 的 onBeforeRender，在渲染反射时隐藏 layer 1 的对象（伤害数字等）
            const originalOnBeforeRender = water.onBeforeRender.bind(water);
            water.onBeforeRender = (renderer, scene, camera) => {
                // 收集并临时隐藏 layer 1 的对象
                const hiddenObjects = [];
                scene.traverse((obj) => {
                    if (obj.visible && obj.layers.isEnabled(1) && !obj.layers.isEnabled(0)) {
                        hiddenObjects.push(obj);
                        obj.visible = false;
                    }
                });

                // 调用原始渲染
                originalOnBeforeRender(renderer, scene, camera);

                // 恢复对象可见性
                hiddenObjects.forEach(obj => obj.visible = true);
            };

            this.ocean = water;
            this.scene.add(this.ocean);
        } catch (e) {
            // Fallback: static, slightly glossy ocean plane
            const oceanMaterial = new THREE.MeshStandardMaterial({
                color: 0x2B61A8,  // 略浅的深海蓝
                metalness: 0.0,
                roughness: 0.9  // 保持较暗、少高光
            });

            this.ocean = new THREE.Mesh(geometry, oceanMaterial);
            this.ocean.rotation.x = -Math.PI / 2;
            this.ocean.position.y = -0.1; // 稍微降低海面高度，避免与沙滩Z-fighting
            this.ocean.receiveShadow = false;
            this.scene.add(this.ocean);

            this.oceanMaterial = oceanMaterial;
        }
    }

    animateOcean(deltaTime = 0.016) {
        // If using Water helper, advance its time uniform very slowly for subtle motion
        if (this.ocean && this.ocean.material && this.ocean.material.uniforms && this.ocean.material.uniforms.time) {
            this.ocean.material.uniforms.time.value += deltaTime * 0.3; // 慢速流动
        }
        // Static fallback needs no animation
    }

    createSkybox() {
        // Create simple gradient skybox
        const skyboxGeometry = new THREE.SphereGeometry(1200, 32, 32);

        // Create gradient material
        const skyboxMaterial = new THREE.ShaderMaterial({
            uniforms: {
                topColor: { value: new THREE.Color(0x1a3d56) },    // 更暗的天空顶部
                bottomColor: { value: new THREE.Color(0x0a2845) }, // 与海面匹配的深蓝色
                offset: { value: 8 },
                exponent: { value: 1.2 }
            },
            vertexShader: `
                varying vec3 vWorldPosition;
                void main() {
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vWorldPosition = worldPosition.xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform vec3 topColor;
                uniform vec3 bottomColor;
                uniform float offset;
                uniform float exponent;
                varying vec3 vWorldPosition;
                void main() {
                    float h = normalize(vWorldPosition + offset).y;
                    gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
                }
            `,
            side: THREE.BackSide
        });

        this.skybox = new THREE.Mesh(skyboxGeometry, skyboxMaterial);
        this.scene.add(this.skybox);
    }

    createGrid() {
        // Create coordinate grid for visual reference
        const gridHelper = new THREE.GridHelper(200, 40, 0x888888, 0x444444);
        gridHelper.rotateX(Math.PI / 2); // Rotate to be horizontal
        gridHelper.position.y = 0.1; // Slightly above ocean
        gridHelper.material.transparent = true;
        gridHelper.material.opacity = 0.3;
        this.scene.add(gridHelper);

        // Add axis helper for direction reference
        const axesHelper = new THREE.AxesHelper(20);
        axesHelper.position.y = 0.2;
        this.scene.add(axesHelper);

        // Add coordinate labels with better visibility
        this.addCoordinateLabels();
    }

    addCoordinateLabels() {
        // Simple letter labels at axis endpoints
        const createAxisLetter = (letter, position, color) => {
            // Small cube for the letter indicator
            const letterGeometry = new THREE.BoxGeometry(1, 1, 1);
            const letterMaterial = new THREE.MeshBasicMaterial({
                color: color
            });
            const letterCube = new THREE.Mesh(letterGeometry, letterMaterial);
            letterCube.position.copy(position);
            this.scene.add(letterCube);
        };

        // X轴尽头（红色） - 轴线尽头放个红色小方块代表X
        createAxisLetter("X", new THREE.Vector3(20, 0, 0), 0xFF0000);

        // Y轴尽头（绿色） - 轴线尽头放个绿色小方块代表Y
        createAxisLetter("Y", new THREE.Vector3(0, 20, 0), 0x00FF00);

        // Z轴尽头（蓝色） - 轴线尽头放个蓝色小方块代表Z
        createAxisLetter("Z", new THREE.Vector3(0, 0, 20), 0x0000FF);

        // Simple origin marker
        const originGeometry = new THREE.SphereGeometry(1);
        const originMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFF00
        });
        const originMarker = new THREE.Mesh(originGeometry, originMaterial);
        originMarker.position.set(0, 0.5, 0);
        this.scene.add(originMarker);
    }

    async createReferenceObjects() {
        // 加载卡通岛屿GLB模型
        try {
            await this.loadCartoonIslandGLB();
        } catch (error) {
            console.warn('Failed to load cartoon island GLB, creating procedural island instead:', error);
            // Fallback to procedural island generation
            const island = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
            this.optimizeCentralIslandMesh(island);
            this.scene.add(island);
            this.updateCentralIslandBounds(island);
            // Add physics for the procedural island
            this.createIslandPhysics({ x: 0, y: 0, z: 0 }, island);
        }

        await this.scatterSmallIslandsWithCharacters(8);
    }

    /**
     * 加载卡通岛屿GLB模型
     */
    async loadCartoonIslandGLB() {
        // Use shared loading manager for aggregated progress
        const loader = new GLTFLoader(modelLoader.loadingManager);
        const modelPath = './models/GLB format/cartoon-island.glb';

        try {
            console.log('🔄 正在加载卡通岛屿模型...', modelPath);

            const gltf = await new Promise((resolve, reject) => {
                loader.load(
                    modelPath,
                    (gltf) => {
                        console.log('✅ GLB模型加载成功');
                        resolve(gltf);
                    },
                    undefined,
                    (error) => {
                        console.error('❌ GLB模型加载失败:', error);
                        reject(error);
                    }
                );
            });

            // 获取岛屿模型
            const islandModel = gltf.scene;

            // 设置模型位置到场景中心
            islandModel.position.set(0, 0, 0);
            islandModel.scale.setScalar(1); // 保持原始大小

            this.optimizeCentralIslandMesh(islandModel);

            // 启用阴影并设置地形网格名称
            let terrainMeshFound = false;
            let maxVertexCount = 0;
            let terrainCandidate = null;
            
            console.log('📊 分析岛屿模型网格结构:');
            islandModel.traverse((child) => {
                if (child.isMesh) {
                    // 减少阴影开销：岛屿不投射阴影，仅接收
                    child.castShadow = false;
                    child.receiveShadow = true;
                    
                    const vertexCount = child.geometry?.attributes?.position?.count || 0;
                    console.log(`  网格: ${child.name || '未命名'}, 顶点数 ${vertexCount}`);
                    
                    // 找到顶点数最多的网格作为地形
                    if (child.geometry && child.geometry.attributes.position && vertexCount > maxVertexCount) {
                        maxVertexCount = vertexCount;
                        terrainCandidate = child;
                    }
                }
            });
            
            // 设置地形网格名称
            if (terrainCandidate) {
                terrainCandidate.name = 'IslandTerrain';
                terrainMeshFound = true;
                console.log('✅ 设置地形网格:', terrainCandidate.name, '顶点数', maxVertexCount);
            } else {
                 console.warn('⚠️ 未找到合适的地形网格，最大顶点数:', maxVertexCount);
             }

            // 添加到场景
            this.scene.add(islandModel);
            this.updateCentralIslandBounds(islandModel);

            // 为GLB岛屿添加物理碰撞体（使用包围盒估算尺寸）
            console.log('⚙️ 开始创建岛屿物理碰撞体...');
            this.createIslandPhysics({ x: 0, y: 0, z: 0 }, islandModel);
            console.log('⚙️ 岛屿物理碰撞体创建完成');

            console.log('✅ 成功加载卡通岛屿GLB模型到场景中心');

        } catch (error) {
            console.error('❌ 加载卡通岛屿GLB模型失败:', error);
            console.log('🔍 请确保文件路径正确', modelPath);

            // 备选方案：创建一个程序化生成的岛屿
            console.log('🚧 使用程序化生成的岛屿作为备选...');
            const fallbackIsland = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
            this.optimizeCentralIslandMesh(fallbackIsland);
            this.scene.add(fallbackIsland);
            this.updateCentralIslandBounds(fallbackIsland);
        }
    }

    /**
     * 创建新的程序化岛屿（无水域，缩小版本）
     */
    createNewProceduralIsland() {
        try {
            console.log('🔄 创建新的无水域岛屿（50%大小）...');

            // 在场景中心创建新岛屿
            const island = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
            this.optimizeCentralIslandMesh(island);
            this.scene.add(island);
            this.updateCentralIslandBounds(island);

            console.log('✅ 成功创建新的无水域岛屿到场景中心');

            // 添加导出功能
            window.exportNewIsland = () => {
                console.log('🔄 导出新的无水域岛屿...');
                const newIsland = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
                this.islandGenerator.exportToGLB(newIsland, 'compact-island-no-water');
            };

            console.log('💡 提示: 在控制台输入 exportNewIsland() 导出新的无水域岛屿');

        } catch (error) {
            console.error('❌ 创建程序化岛屿失败', error);
        }
    }

    async createBuoyMarkers() {
        // Create cargo piles as visual reference points using actual 3D models
        const cargoPositions = [
            { x: 30, z: 30 },
            { x: -40, z: 20 },
            { x: 50, z: -30 },
            { x: -20, z: -40 },
            { x: 0, z: 60 },
            { x: -60, z: 0 }
        ];

        for (let i = 0; i < cargoPositions.length; i++) {
            const pos = cargoPositions[i];
            const modelName = 'cargo-pile-a'; // 缁熶竴浣跨敤璐х墿鍫嗘ā鍨?

            try {
                const cargoModel = await modelLoader.loadModel(modelName, {
                    scale: 1.5, // 閫傚綋缂╂斁璐х墿鍫?
                    position: { x: pos.x, y: 0, z: pos.z },
                    rotation: { x: 0, y: Math.random() * Math.PI * 2, z: 0 },
                    castShadow: true,
                    receiveShadow: true
                });

                this.scene.add(cargoModel);

                // 涓鸿揣鐗╁爢娣诲姞鐗╃悊纰版挒浣?
                this.createBuoyPhysics(pos, cargoModel);

                console.log(`鉁?Loaded cargo-pile-a model at (${pos.x}, ${pos.z})`);
            } catch (error) {
                console.warn(`鈿狅笍 Failed to load cargo-pile-a model, falling back to simple geometry`);
                this.createFallbackBuoy(pos, i);
            }
        }
    }

    updateCentralIslandBounds(mesh) {
        if (!mesh) {
            this.centralIslandBounds = null;
            return;
        }

        const bbox = new THREE.Box3().setFromObject(mesh);
        if (bbox.isEmpty()) {
            this.centralIslandBounds = null;
            return;
        }

        const center = new THREE.Vector3();
        bbox.getCenter(center);
        const size = new THREE.Vector3();
        bbox.getSize(size);
        const horizontalRadius = Math.sqrt(size.x * size.x + size.z * size.z) * 0.5;

        this.centralIslandBounds = {
            center,
            bbox,
            radius: horizontalRadius,
            halfExtents: { x: size.x * 0.5, z: size.z * 0.5 }
        };
    }

    async createCargoIslands() {
        // Create "islands" using cargo containers and piles for a more realistic harbor environment
        const cargoIslandConfigs = [
            {
                x: 80, z: 80,
                containers: [
                    { model: 'cargo-container-a', offset: { x: 0, z: 0 } },
                    { model: 'cargo-container-b', offset: { x: 8, z: 0 } },
                    { model: 'cargo-pile-a', offset: { x: 4, z: 8 } }
                ]
            },
            {
                x: -70, z: 90,
                containers: [
                    { model: 'cargo-container-c', offset: { x: 0, z: 0 } },
                    { model: 'cargo-pile-b', offset: { x: 6, z: 6 } }
                ]
            },
            {
                x: 100, z: -60,
                containers: [
                    { model: 'cargo-container-a', offset: { x: 0, z: 0 } },
                    { model: 'cargo-container-b', offset: { x: 0, z: 8 } },
                    { model: 'cargo-container-c', offset: { x: 8, z: 0 } },
                    { model: 'cargo-pile-a', offset: { x: 8, z: 8 } }
                ]
            }
        ];

        for (const island of cargoIslandConfigs) {
            for (const container of island.containers) {
                try {
                    const containerModel = await modelLoader.loadModel(container.model, {
                        scale: 1.5,
                        position: {
                            x: island.x + container.offset.x,
                            y: 0,
                            z: island.z + container.offset.z
                        },
                        rotation: { x: 0, y: Math.random() * Math.PI * 2, z: 0 },
                        castShadow: true,
                        receiveShadow: true
                    });

                    this.scene.add(containerModel);

                    // 涓鸿揣鐗╁鍣ㄦ坊鍔犵墿鐞嗙鎾炰綋
                    this.createCargoPhysics(
                        {
                            x: island.x + container.offset.x,
                            z: island.z + container.offset.z,
                            size: 6
                        },
                        containerModel
                    );

                    console.log(`鉁?Loaded cargo model ${container.model} at (${island.x + container.offset.x}, ${island.z + container.offset.z})`);
                } catch (error) {
                    console.warn(`鈿狅笍 Failed to load cargo model ${container.model}, falling back to simple geometry`);
                    this.createFallbackCargo(
                        {
                            x: island.x + container.offset.x,
                            z: island.z + container.offset.z,
                            size: 6
                        }
                    );
                }
            }
        }
    }

    // Fallback method for buoys if model loading fails
    createFallbackBuoy(pos, index) {
        const buoyGeometry = new THREE.CylinderGeometry(1, 1.5, 3);
        const buoyMaterial = new THREE.MeshLambertMaterial({
            color: index % 2 === 0 ? 0xFF4444 : 0x44FF44
        });

        const buoy = new THREE.Mesh(buoyGeometry, buoyMaterial);
        buoy.position.set(pos.x, 1, pos.z);
        buoy.castShadow = true;

        this.scene.add(buoy);
        this.createBuoyPhysics(pos, buoy);
    }

    /**
     * 鍒涘缓绋嬪簭鍖栫敓鎴愮殑娴峰矝
     */
    createProceduralIslands() {
        // 瀹氫箟娴峰矝浣嶇疆锛堣繙绂绘父鎴忎腑蹇冨尯鍩燂級
        const islandPositions = [
            { x: 70, z: 60 },   // 涓滃寳鏂瑰悜
            { x: -80, z: 70 },  // 瑗垮寳鏂瑰悜
            { x: 60, z: -75 },  // 涓滃崡鏂瑰悜
            { x: -65, z: -60 }  // 瑗垮崡鏂瑰悜
        ];

        islandPositions.forEach((pos, index) => {
            try {
                const island = this.islandGenerator.createIsland(pos);
                this.scene.add(island);

                // 涓烘捣宀涙坊鍔犵墿鐞嗙鎾炰綋
                this.createIslandPhysics(pos, island);

                console.log(`鉁?Created procedural island ${index + 1} at (${pos.x}, ${pos.z}) with debug marker`);
            } catch (error) {
                console.warn(`鈿狅笍 Failed to create procedural island ${index + 1}:`, error);
            }
        });

        // 娣诲姞鍏ㄥ眬瀵煎嚭鍑芥暟渚涙帶鍒跺彴浣跨敤
        window.exportIslands = () => {
            console.log('馃幆 寮€濮嬪鍑烘墍鏈夋捣宀涙ā鍨?..');
            this.islandGenerator.exportAllIslands();
        };

        window.exportSingleIsland = (filename = 'tropical-island') => {
            console.log(`馃幆 瀵煎嚭楂樿川閲忔捣宀涙ā鍨? ${filename}`);
            const island = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
            this.islandGenerator.exportToGLB(island, filename);
        };

        // 绠€鍖栫増鏈?- 鍙鍑轰竴涓珮璐ㄩ噺娴峰矝
        window.exportIsland = () => {
            console.log('馃彎锔?瀵煎嚭楂樿川閲忕儹甯︽捣宀?..');
            const island = this.islandGenerator.createIsland({ x: 0, y: 0, z: 0 });
            this.islandGenerator.exportToGLB(island, 'high-quality-tropical-island');
        };

        console.log('馃挕 鎻愮ず: 鍦ㄦ帶鍒跺彴杈撳叆 exportIsland() 瀵煎嚭楂樿川閲忔捣宀涳紝鎴?exportSingleIsland("鍚嶇О") 鑷畾涔夋枃浠跺悕');
    }

    async scatterSmallIslandsWithCharacters(count = 8) {
        const placements = this.generateIslandPlacementPoints(count);
        this.ambientIslands = [];

        for (let index = 0; index < placements.length; index++) {
            const placement = placements[index];
            try {
                const island = await modelLoader.loadModel('patch-sand-foliage', {
                    scale: placement.scale,
                    position: { x: placement.x, y: placement.y, z: placement.z },
                    rotation: { x: 0, y: placement.rotation, z: 0 },
                    castShadow: false,
                    receiveShadow: true
                });

                island.name = `AmbientIsland_${index + 1}`;
                this.scene.add(island);
                island.updateMatrixWorld(true);

                const resident = await this.attachSimpleIslander(island);
                this.addAmbientIslandPhysics(island);

                this.ambientIslands.push({
                    id: island.name,
                    mesh: island,
                    resident,
                    position: new THREE.Vector3(placement.x, placement.y, placement.z)
                });
            } catch (error) {
                console.warn(`Failed to spawn ambient island ${index + 1}:`, error);
            }
        }
    }

    generateIslandPlacementPoints(count) {
        const placements = [];
        const minDistanceFromCenter = 55;
        const maxRadius = GAME_BOUNDARY_SIZE - 18;
        const minSpacing = 28;
        const maxAttempts = count * 20;
        let attempts = 0;

        while (placements.length < count && attempts < maxAttempts) {
            attempts++;
            const angle = Math.random() * Math.PI * 2;
            const radius = THREE.MathUtils.lerp(minDistanceFromCenter, maxRadius, Math.random());
            const x = Math.cos(angle) * radius;
            const z = Math.sin(angle) * radius;

            const isTooClose = placements.some((p) => {
                const dx = p.x - x;
                const dz = p.z - z;
                return dx * dx + dz * dz < minSpacing * minSpacing;
            });

            if (isTooClose) {
                continue;
            }

            placements.push({
                x,
                y: -0.25,
                z,
                rotation: Math.random() * Math.PI * 2,
                scale: THREE.MathUtils.randFloat(0.75, 1.25)
            });
        }

        return placements;
    }

    async attachSimpleIslander(island) {
        if (!island) return null;

        island.updateMatrixWorld(true);
        const bbox = new THREE.Box3().setFromObject(island);
        const size = new THREE.Vector3();
        bbox.getSize(size);

        const islander = await this.createSimpleIslander();
        if (!islander) return null;
        const topY = bbox.max.y - island.position.y;
        const walkRadius = Math.min(size.x, size.z) * 0.25;
        const angle = Math.random() * Math.PI * 2;
        const offsetX = Math.cos(angle) * walkRadius;
        const offsetZ = Math.sin(angle) * walkRadius;

        islander.position.set(offsetX, topY + 0.05, offsetZ);
        islander.rotation.y = Math.random() * Math.PI * 2;
        islander.name = 'IslandResident';
        islander.userData.islandResident = true;
        islander.traverse((child) => {
            if (child.isMesh) {
                child.userData.islandResident = true;
            }
        });

        island.add(islander);
        return islander;
    }

    async createSimpleIslander() {
        try {
            // 随机选择 man.glb 或 man2.glb
            const modelNames = ['man', 'man2'];
            const randomModel = modelNames[Math.floor(Math.random() * modelNames.length)];

            // 使用 GLTFLoader 加载模型（路径相对于 public 目录）
            const loader = new GLTFLoader();
            const modelPath = `/models/GLB format/ship/${randomModel}.glb`;

            const gltf = await new Promise((resolve, reject) => {
                loader.load(
                    modelPath,
                    (gltf) => resolve(gltf),
                    undefined,
                    (error) => reject(error)
                );
            });

            const model = gltf.scene;
            model.userData.islandResident = true;

            // 设置阴影和提高亮度
            model.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.userData.islandResident = true;

                    // 提高材质亮度
                    if (child.material) {
                        // 如果是数组材质
                        const materials = Array.isArray(child.material) ? child.material : [child.material];
                        materials.forEach(mat => {
                            if (mat) {
                                // 大幅增加自发光来提高亮度
                                mat.emissive = mat.color ? mat.color.clone() : new THREE.Color(0xffffff);
                                mat.emissiveIntensity = 0.8; // 提高到0.8
                                // 减少粗糙度，增加光泽
                                if (mat.roughness !== undefined) {
                                    mat.roughness = 0.2;
                                }
                                // 增加金属度以增强反光
                                if (mat.metalness !== undefined) {
                                    mat.metalness = 0.3;
                                }
                            }
                        });
                    }
                }
            });

            // 调整模型大小（原来1/4的2倍 = 1/2）
            model.scale.set(0.25, 0.25, 0.25);

            return model;
        } catch (error) {
            console.warn('Failed to load character model, using fallback:', error);
            // 如果加载失败，返回简单的备用模型
            return this.createFallbackIslander();
        }
    }

    createFallbackIslander() {
        const group = new THREE.Group();
        group.userData.islandResident = true;

        const palettes = [
            0x2563eb,
            0xec4899,
            0xf97316,
            0x22c55e,
            0x8b5cf6
        ];
        const bodyColor = palettes[Math.floor(Math.random() * palettes.length)];

        const baseMaterial = new THREE.MeshStandardMaterial({
            color: bodyColor,
            roughness: 0.6,
            metalness: 0.1
        });

        const headMaterial = new THREE.MeshStandardMaterial({
            color: 0xfff2d5,
            roughness: 0.7,
            metalness: 0.0
        });

        const baseGeometry = new THREE.CylinderGeometry(0.55, 0.7, 0.25, 12);
        const baseMesh = new THREE.Mesh(baseGeometry, baseMaterial);
        baseMesh.position.y = 0.12;
        baseMesh.castShadow = true;
        baseMesh.receiveShadow = true;
        group.add(baseMesh);

        const bodyGeometry = new THREE.CylinderGeometry(0.35, 0.38, 1.2, 14);
        const bodyMesh = new THREE.Mesh(bodyGeometry, baseMaterial);
        bodyMesh.position.y = 0.9;
        bodyMesh.castShadow = true;
        bodyMesh.receiveShadow = true;
        group.add(bodyMesh);

        const headGeometry = new THREE.SphereGeometry(0.32, 16, 16);
        const headMesh = new THREE.Mesh(headGeometry, headMaterial);
        headMesh.position.y = 1.65;
        headMesh.castShadow = true;
        headMesh.receiveShadow = true;
        group.add(headMesh);

        const armGeometry = new THREE.CylinderGeometry(0.12, 0.12, 0.8, 12);
        const leftArm = new THREE.Mesh(armGeometry, baseMaterial);
        leftArm.position.set(-0.42, 0.95, 0);
        leftArm.rotation.z = Math.PI / 8;
        leftArm.castShadow = true;
        group.add(leftArm);

        const rightArm = leftArm.clone();
        rightArm.position.x = 0.42;
        rightArm.rotation.z = -Math.PI / 8;
        group.add(rightArm);

        group.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                child.userData.islandResident = true;
            }
        });

        return group;
    }

    addAmbientIslandPhysics(island) {
        if (!this.physicsSystem || !island) return;

        const bbox = new THREE.Box3().setFromObject(island);
        const size = new THREE.Vector3();
        bbox.getSize(size);

        if (size.x <= 0.1 || size.z <= 0.1) {
            return;
        }

        const family = [];
        island.traverse((child) => {
            if (child.userData?.islandResident || child.parent?.userData?.islandResident) {
                return;
            }
            if (child.isMesh && child.geometry) {
                family.push(child);
            }
        });

        if (!family.length) {
            return;
        }

        const center = new THREE.Vector3();
        bbox.getCenter(center);

        const vertices = [];
        const indices = [];
        let vertexOffset = 0;
        const position = new THREE.Vector3();

        family.forEach((mesh) => {
            const geometry = mesh.geometry;
            const positionAttr = geometry.getAttribute('position');
            if (!positionAttr) return;

            const indexAttr = geometry.getIndex();
            const matrixWorld = mesh.matrixWorld;

            for (let i = 0; i < positionAttr.count; i++) {
                position.fromBufferAttribute(positionAttr, i);
                position.applyMatrix4(matrixWorld);
                position.sub(center);
                vertices.push(position.x, position.y, position.z);
            }

            if (indexAttr) {
                for (let i = 0; i < indexAttr.count; i += 3) {
                    indices.push(
                        vertexOffset + indexAttr.getX(i),
                        vertexOffset + indexAttr.getX(i + 1),
                        vertexOffset + indexAttr.getX(i + 2)
                    );
                }
            } else {
                for (let i = 0; i < positionAttr.count; i += 3) {
                    indices.push(
                        vertexOffset + i,
                        vertexOffset + i + 1,
                        vertexOffset + i + 2
                    );
                }
            }

            vertexOffset += positionAttr.count;
        });

        if (vertices.length < 9 || indices.length < 3) {
            return;
        }

        const triMesh = new CANNON.Trimesh(new Float32Array(vertices), new Uint32Array(indices));

        const body = new CANNON.Body({
            mass: 0,
            material: this.physicsSystem.materials?.boundary || null
        });
        body.addShape(triMesh);
        body.position.set(center.x, center.y, center.z);

        this.physicsSystem.addBody(body);
        this.physicsBodies.push(body);
        island.userData.physicsBody = body;
    }

    getAmbientIslands() {
        return this.ambientIslands || [];
    }

    updateCentralIslandBounds(mesh) {
        if (!mesh) {
            this.centralIslandBounds = null;
            return;
        }

        const bbox = new THREE.Box3().setFromObject(mesh);
        if (bbox.isEmpty()) {
            this.centralIslandBounds = null;
            return;
        }

        const center = new THREE.Vector3();
        bbox.getCenter(center);
        const size = new THREE.Vector3();
        bbox.getSize(size);
        const horizontalRadius = Math.sqrt(size.x * size.x + size.z * size.z) * 0.5;

        this.centralIslandBounds = {
            center,
            bbox,
            radius: horizontalRadius,
            halfExtents: { x: size.x * 0.5, z: size.z * 0.5 }
        };
    }

    optimizeCentralIslandMesh(mesh) {
        if (!mesh || !this.simplifyModifier) return;

        const simplify = this.simplifyModifier;
        const tempColor = new THREE.Color(0xffffff);

        mesh.traverse((child) => {
            if (!child.isMesh || !child.geometry) return;

            child.castShadow = false;
            child.receiveShadow = false;

            const geometry = child.geometry;
            const positionAttr = geometry.getAttribute('position');
            if (positionAttr) {
                const count = positionAttr.count;
                // 性能优化：更激进的简化策略
                let targetCount = null;
                if (count > 8000) {
                    targetCount = Math.max(2000, Math.floor(count * 0.20)); // 从40%降到20%
                } else if (count > 4000) {
                    targetCount = Math.max(1500, Math.floor(count * 0.25)); // 中等网格保留25%
                }

                if (targetCount) {
                    let source = geometry;
                    if (geometry.index) {
                        source = geometry.toNonIndexed();
                    }
                    try {
                        const simplified = simplify.modify(source, targetCount);
                        simplified.computeVertexNormals();
                        if (geometry !== simplified) {
                            geometry.dispose?.();
                            child.geometry = simplified;
                        }
                        console.log(`✅ 简化岛屿网格: ${count} → ${targetCount} 顶点 (${((targetCount/count)*100).toFixed(1)}%)`);
                    } catch (error) {
                        console.warn('Simplify island mesh failed', error);
                    }
                }
            }

            if (child.material && child.material.dispose) {
                const original = child.material;
                const newMaterial = original.clone();
                newMaterial.flatShading = true;
                newMaterial.needsUpdate = true;
                child.material = newMaterial;
                original.dispose();
            }
       });
    }

    createIslandPhysics(position, mesh) {
        // 检查是否应该使用简化的岛屿物理（通过URL参数控制）
        // 默认情况下，我们应该尽可能使用精确的碰撞检测
        if (this.useSimplifiedIslandPhysics) {
            console.log('⚠️ 使用简化的岛屿物理（圆柱体近似）');
            const box3 = new THREE.Box3().setFromObject(mesh);
            const size = new THREE.Vector3();
            box3.getSize(size);
            const radius = Math.max(size.x, size.z) * 0.5 + 2;
            const height = Math.max(3, Math.min(8, size.y + 1));
            const shape = new CANNON.Cylinder(radius, radius, height, 12);
            
            const body = new CANNON.Body({
                mass: 0,
                material: this.physicsSystem.materials.boundary
            });
            body.addShape(shape);
            
            const worldPosition = new THREE.Vector3();
            mesh.getWorldPosition(worldPosition);
            body.position.set(worldPosition.x, worldPosition.y + height / 2, worldPosition.z);
            body.collisionFilterGroup = 8; // 岛屿/建筑组
            body.collisionFilterMask = 1 | 2 | 4; // 与边界、船只、炮弹碰撞
            
            this.physicsSystem.addBody(body);
            this.physicsBodies.push(body);
            mesh.userData.physicsBody = body;
            return;
        }
        console.log('⚙️ createIslandPhysics 被调用，位置:', position);
        console.log('📊 检查物理系统状态');
        console.log('- physicsSystem存在:', !!this.physicsSystem);
        console.log('- physicsSystem.world存在:', !!(this.physicsSystem && this.physicsSystem.world));
        
        if (!this.physicsSystem) {
            console.error('❌ Physics system not available for island physics');
            return;
        }
        
        if (!this.physicsSystem.world) {
            console.error('❌ Physics world not available - world object is null/undefined');
            console.log('- physicsSystem对象:', this.physicsSystem);
            console.log('- physicsSystem.world:', this.physicsSystem.world);
            return;
        }
        
        console.log('✅ 物理世界可访问，继续创建岛屿物理体');

        // 查找地形网格
        let terrainMesh = null;
        console.log('📊 开始查找地形网格...');
        
        if (mesh.name === 'IslandTerrain') {
            terrainMesh = mesh;
        } else {
            // 递归查找名为 'IslandTerrain' 的子网格
            mesh.traverse((child) => {
                if (child.name === 'IslandTerrain' && child.geometry) {
                    terrainMesh = child;
                }
            });
        }

        if (!terrainMesh || !terrainMesh.geometry) {
            console.warn('❌ 未找到地形网格，使用整个岛屿模型创建Trimesh碰撞体');
            console.log('调试信息 - 网格结构:');
            mesh.traverse((child) => {
                if (child.isMesh) {
                    console.log('  网格名称:', child.name, '顶点数', child.geometry?.attributes?.position?.count || 0);
                }
            });
            
            // 使用整个岛屿模型创建Trimesh碰撞体
            const geometry = mesh.geometry || (mesh.children.length > 0 ? mesh.children[0].geometry : null);
            if (geometry) {
                const vertices = [];
                const indices = [];
                
                // 确保几何体已更新
                geometry.computeBoundingBox();
                geometry.computeVertexNormals();
                
                // 获取顶点数据（使用本地坐标）
                const positionAttribute = geometry.getAttribute('position');
                if (positionAttribute) {
                    for (let i = 0; i < positionAttribute.count; i++) {
                        vertices.push(
                            positionAttribute.getX(i),
                            positionAttribute.getY(i),
                            positionAttribute.getZ(i)
                        );
                    }
                    
                    // 获取索引数据
                    const indexAttribute = geometry.getIndex();
                    if (indexAttribute) {
                        for (let i = 0; i < indexAttribute.count; i++) {
                            indices.push(indexAttribute.getX(i));
                        }
                    } else {
                        // 如果没有索引，创建默认索引
                        for (let i = 0; i < positionAttribute.count; i++) {
                            indices.push(i);
                        }
                    }
                    
                    // 创建 Trimesh 形状
                    const trimeshShape = new CANNON.Trimesh(vertices, indices);
                    
                    // 创建物理体
                    const body = new CANNON.Body({
                        mass: 0,
                        material: this.physicsSystem.materials.boundary
                    });
                    
                    body.addShape(trimeshShape);
                    
                    // 设置物理体位置（考虑网格的世界位置）
                    const worldPosition = new THREE.Vector3();
                    mesh.getWorldPosition(worldPosition);
                    body.position.set(
                        worldPosition.x,
                        worldPosition.y,
                        worldPosition.z
                    );
                    
                    body.collisionFilterGroup = 8;
                    body.collisionFilterMask = 1 | 2 | 4; // 与边界、船只和炮弹碰撞
                    
                    this.physicsSystem.addBody(body);
                    this.physicsBodies.push(body);
                    mesh.userData.physicsBody = body;
                    
                    console.log('✅ 成功创建基于整个岛屿模型的 Trimesh 碰撞体');
                    console.log('  - 顶点数', vertices.length / 3);
                    console.log('  - 索引数', indices.length);
                    return;
                }
            }
            
            // 最后的回退方案：使用简单的圆柱体碰撞体
            console.warn('⚠️ 无法创建Trimesh碰撞体，使用默认圆柱体碰撞体');
            const box3 = new THREE.Box3().setFromObject(mesh);
            const size = new THREE.Vector3();
            box3.getSize(size);
            const radius = Math.max(size.x, size.z) * 0.5 + 2;
            const height = Math.max(3, Math.min(8, size.y + 1));
            const shape = new CANNON.Cylinder(radius, radius, height, 12);
            
            const body = new CANNON.Body({
                mass: 0,
                material: this.physicsSystem.materials.boundary
            });
            body.addShape(shape);
            // 获取网格的世界位置
            const worldPosition = new THREE.Vector3();
            mesh.getWorldPosition(worldPosition);
            body.position.set(worldPosition.x, worldPosition.y + height / 2, worldPosition.z);
            body.collisionFilterGroup = 8;
            body.collisionFilterMask = 1 | 2 | 4; // 修复掩码，包含边界碰撞
            this.physicsSystem.addBody(body);
            this.physicsBodies.push(body);
            mesh.userData.physicsBody = body;
            return;
        }

        // 使用地形网格创建 Trimesh 碰撞体
        const geometry = terrainMesh.geometry;
        const vertices = [];
        const indices = [];

        // 确保几何体已更新
        geometry.computeBoundingBox();
        geometry.computeVertexNormals();

        // 获取顶点数据（使用本地坐标）
        const positionAttribute = geometry.getAttribute('position');
        for (let i = 0; i < positionAttribute.count; i++) {
            vertices.push(
                positionAttribute.getX(i),
                positionAttribute.getY(i),
                positionAttribute.getZ(i)
            );
        }
        console.log('📊 地形网格顶点数据:', vertices.length / 3, '个顶点');

        // 获取索引数据
        const indexAttribute = geometry.getIndex();
        if (indexAttribute) {
            for (let i = 0; i < indexAttribute.count; i++) {
                indices.push(indexAttribute.getX(i));
            }
        } else {
            // 如果没有索引，创建默认索引
            for (let i = 0; i < positionAttribute.count; i++) {
                indices.push(i);
            }
        }

        // 创建 Trimesh 形状
        const trimeshShape = new CANNON.Trimesh(vertices, indices);

        // 创建物理体
        const body = new CANNON.Body({
            mass: 0,
            material: this.physicsSystem.materials.boundary
        });

        body.addShape(trimeshShape);

        // 设置物理体位置（考虑地形网格的世界位置）
        // 获取地形网格的世界位置
        const worldPosition = new THREE.Vector3();
        terrainMesh.getWorldPosition(worldPosition);
        
        body.position.set(
            worldPosition.x,
            worldPosition.y,
            worldPosition.z
        );
        console.log('📍 物理体位置设置为:', body.position);
        console.log('📍 地形网格世界位置:', worldPosition);

        body.collisionFilterGroup = 8; // 建筑物碰撞组
        body.collisionFilterMask = 1 | 2 | 4; // 与边界、船只和炮弹碰撞

        this.physicsSystem.addBody(body);
        this.physicsBodies.push(body);
        mesh.userData.physicsBody = body;
        
        // 验证碰撞体是否正确添加到物理世界
        const worldBodies = this.physicsSystem.world.bodies.length;
        console.log('✅ 成功创建基于地形网格的 Trimesh 碰撞体');
        console.log('  - 顶点数', vertices.length / 3);
        console.log('  - 索引数', indices.length);
        console.log('  - 地形网格名称:', terrainMesh.name);
        console.log('  - 物理世界中的物体总数:', worldBodies);
        console.log('  - 碰撞组', body.collisionFilterGroup, '碰撞掩码:', body.collisionFilterMask);
        
        // 生产环境不输出调试信息
    }

    // Fallback method for cargo if model loading fails
    createFallbackCargo(config) {
        const cargoGeometry = new THREE.BoxGeometry(config.size, config.size, config.size);
        const cargoMaterial = new THREE.MeshLambertMaterial({
            color: 0x8B4513
        });

        const cargoMesh = new THREE.Mesh(cargoGeometry, cargoMaterial);
        cargoMesh.position.set(config.x, config.size / 2, config.z);
        cargoMesh.castShadow = true;
        cargoMesh.receiveShadow = true;

        this.scene.add(cargoMesh);
        this.createCargoPhysics(config, cargoMesh);
    }

    // 为浮标创建物理碰撞体
    createBuoyPhysics(position, mesh) {
        if (!this.physicsSystem) return;

        const shape = new CANNON.Cylinder(1, 1.5, 3, 8);
        const body = new CANNON.Body({
            mass: 0, // 静态物体
            material: this.physicsSystem.materials.boundary
        });

        body.addShape(shape);
        body.position.set(position.x, 1, position.z);
        body.collisionFilterGroup = 8; // 建筑物碰撞组
        body.collisionFilterMask = 2 | 4; // 与船只和炮弹碰撞

        this.physicsSystem.addBody(body);
        this.physicsBodies.push(body);

        // 将物理体引用存储到mesh中
        mesh.userData.physicsBody = body;
    }

    // 重复的createIslandPhysics方法已删除，使用第一个Trimesh版本
    createCargoPhysics(config, mesh) {
        if (!this.physicsSystem) return;

        // 使用Box形状替代，确保更可靠的碰撞检测
        const size = config.size || 6;
        const shape = new CANNON.Box(new CANNON.Vec3(size, size, size));
        const body = new CANNON.Body({
            mass: 0, // 静态物体
            material: this.physicsSystem.materials.boundary
        });

        body.addShape(shape);
        body.position.set(config.x, size / 2, config.z); // Y位置设为半高度
        body.collisionFilterGroup = 8; // 建筑物碰撞组
        body.collisionFilterMask = 2 | 4; // 与船只和炮弹碰撞

        this.physicsSystem.addBody(body);
        this.physicsBodies.push(body);

        // 将物理体引用存储到mesh中
        mesh.userData.physicsBody = body;
    }

    // 璁剧疆鐗╃悊绯荤粺寮曠敤
    setPhysicsSystem(physicsSystem) {
        this.physicsSystem = physicsSystem;
    }

    update(deltaTime) {
        // Animate ocean (if dynamic water is active)
        this.animateOcean(deltaTime);

        // Update camera to follow target
        if (this.followTarget) {
            this.updateCameraFollow();
        }
    }

    applyCameraDeviceConfig() {
        const useMobile = isMobileDevice();
        const targetConfig = useMobile ? this.mobileCameraConfig : this.defaultCameraConfig;
        const mode = useMobile ? 'mobile' : 'desktop';

        if (this.currentCameraConfig !== mode || !this.cameraOffset.equals(targetConfig.offset)) {
            this.cameraOffset.copy(targetConfig.offset);
        }

        if (this.camera) {
            const targetFov = targetConfig.fov;
            if (this.camera.fov !== targetFov) {
                this.camera.fov = targetFov;
                this.camera.updateProjectionMatrix();
            }
        }

        this.currentCameraConfig = mode;
    }

    updateCameraFollow() {
        if (!this.followTarget) return;

        // Calculate desired camera position
        const targetPosition = this.followTarget.position.clone();
        const desiredCameraPosition = targetPosition.clone().add(this.cameraOffset);

        // Smoothly move camera
        this.camera.position.lerp(desiredCameraPosition, this.cameraLerpFactor);

        // Calculate look-at position and平滑过渡
        const lookAtPosition = targetPosition.clone().add(this.lookAtOffset);
        if (this._currentLookAt.lengthSq() === 0) {
            this._currentLookAt.copy(lookAtPosition);
        } else {
            this._currentLookAt.lerp(lookAtPosition, this.lookAtLerpFactor);
        }
        this.camera.lookAt(this._currentLookAt);
    }

    setFollowTarget(target) {
        this.followTarget = target;
    }

    setCameraOffset(offset) {
        this.cameraOffset.copy(offset);
    }

    setLookAtOffset(offset) {
        this.lookAtOffset.copy(offset);
    }

    handleResize() {
        this.applyCameraDeviceConfig();

        if (this.camera) {
            this.camera.aspect = window.innerWidth / window.innerHeight;
            this.camera.updateProjectionMatrix();
        }
    }

    // Convert screen coordinates to world coordinates for 2D game
    screenToWorld(screenX, screenY) {
        if (!this.camera) return new THREE.Vector3();

        // Simple 2D coordinate mapping for X-Z plane (淇涓婁笅绉诲姩鏂瑰悜)
        const pointerRange = GAME_BOUNDARY_SIZE * 0.75;
        const worldX = (screenX / window.innerWidth - 0.5) * pointerRange; // 宸﹀彸
        const worldZ = (screenY / window.innerHeight - 0.5) * pointerRange; // 鍓嶅悗锛堜笉缈昏浆锛?

        // Return as 3D vector with Y=0 (鑸瑰彧鍦ㄦ捣骞抽潰楂樺害)
        return new THREE.Vector3(worldX, 0, worldZ);
    }

    // Get the scene bounds for physics world
    getSceneBounds() {
        const diameter = GAME_BOUNDARY_SIZE * 2;
        return {
            width: diameter,
            height: diameter,
            depth: 50
        };
    }

    // Get game play area bounds (红色边界内的可玩区域)
    getGameBounds() {
        return {
            minX: -GAME_BOUNDARY_SIZE,
            maxX: GAME_BOUNDARY_SIZE,
            minZ: -GAME_BOUNDARY_SIZE,
            maxZ: GAME_BOUNDARY_SIZE
        };
    }

    // Check if position is within game boundaries
    isWithinGameBounds(position) {
        const bounds = this.getGameBounds();
        return position.x >= bounds.minX && position.x <= bounds.maxX &&
               position.z >= bounds.minZ && position.z <= bounds.maxZ;
    }

    // Clamp position to game boundaries (边界限制功能)
    clampToGameBounds(position) {
        const bounds = this.getGameBounds();
        const clampedPosition = position.clone();

        clampedPosition.x = Math.max(bounds.minX, Math.min(bounds.maxX, position.x));
        clampedPosition.z = Math.max(bounds.minZ, Math.min(bounds.maxZ, position.z));

        return clampedPosition;
    }

    // Add object to scene
    add(object) {
        this.scene.add(object);
    }

    // Remove object from scene
    remove(object) {
        this.scene.remove(object);
    }
}
